// src/services/dataPersistence/utils/storageUtils.ts
// Utilities for storing and retrieving data from various storage mechanisms

/**
 * Save data to localStorage
 */
export function saveToLocalStorage<T>(key: string, data: T): boolean {
    try {
      const serialized = JSON.stringify(data);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }
  
  /**
   * Load data from localStorage
   */
  export function loadFromLocalStorage<T>(key: string): T | null {
    try {
      const serialized = localStorage.getItem(key);
      if (!serialized) return null;
      return JSON.parse(serialized) as T;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }
  
  /**
   * Remove data from localStorage
   */
  export function removeFromLocalStorage(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }
  
  /**
   * Create a throttled function that won't be triggered more than once in {delay} ms
   */
  export function throttle<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let lastCall = 0;
    let timeoutId: number | null = null;
    
    return (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCall;
      
      if (timeSinceLastCall >= delay) {
        lastCall = now;
        func(...args);
      } else {
        // Clear any existing timeout
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        
        // Schedule to run after remaining time
        timeoutId = window.setTimeout(() => {
          lastCall = Date.now();
          func(...args);
          timeoutId = null;
        }, delay - timeSinceLastCall);
      }
    };
  }
  
  /**
   * Create a debounced function that delays invoking func until after wait milliseconds
   */
  export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: number | null = null;
    
    return (...args: Parameters<T>) => {
      // Clear previous timeout
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      
      // Set new timeout
      timeoutId = window.setTimeout(() => {
        func(...args);
        timeoutId = null;
      }, wait);
    };
  }